import { useState } from 'react';
import { ChevronRight, FileText, History, Plus, Sparkles, Trash2 } from 'lucide-react';
import { BLOCK_TYPES, BLOCK_TYPE_LABELS, type Block, type PageSummary, type Workspace } from '@unitly/contracts';
import {
  useCreatePage, useDeletePage, usePage, usePages, usePageTemplates, usePageVersions, useRestorePageVersion, useUpdatePage,
} from '../../api/queries.ts';
import { useOrgCtx } from '../../app/org-context.tsx';
import { Sheet } from '../../ui/Sheet.tsx';
import { EmptyState, PageLoader, Spinner } from '../../ui/primitives.tsx';
import { useToast } from '../../ui/Toast.tsx';
import { ApiError } from '../../api/client.ts';

const ICONS = ['📄', '📘', '📝', '📋', '💡', '🎯', '📌', '🗂️'];

function newBlock(type: Block['type'] = 'paragraph'): Block {
  return { id: Math.random().toString(36).slice(2, 10), type, text: '', checked: type === 'checklist_item' ? false : undefined };
}

/** Documents: a per-workspace page tree with a simple block editor. Always available (core module). */
export function PagesView({ workspace }: { workspace: Workspace }) {
  const { orgId } = useOrgCtx();
  const [stack, setStack] = useState<{ id: string; title: string }[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const parentId = stack.length ? stack[stack.length - 1]!.id : null;
  const pages = usePages(orgId, workspace.id, parentId);
  const templates = usePageTemplates(orgId, workspace.id);
  const create = useCreatePage(orgId);
  const toast = useToast();

  const openPage = (p: PageSummary) => {
    if (p.hasChildren) setStack((s) => [...s, { id: p.id, title: p.title || 'Senza titolo' }]);
    else setOpenId(p.id);
  };

  const makePage = (fromTemplateId?: string) => {
    create.mutate({ workspaceId: workspace.id, parentPageId: parentId, fromTemplateId }, {
      onSuccess: (page) => { setShowTemplates(false); setOpenId(page.id); },
      onError: (e) => toast(e instanceof ApiError ? e.message : 'Creazione non riuscita'),
    });
  };

  return (
    <>
      {stack.length > 0 && (
        <div className="toolbar" style={{ gap: 4, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-plain btn-sm" onClick={() => setStack([])}>Pagine</button>
          {stack.map((s, i) => (
            <span key={s.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <ChevronRight size={14} color="var(--label-3)" />
              <button type="button" className="btn btn-plain btn-sm" onClick={() => setStack(stack.slice(0, i + 1))}>{s.title}</button>
            </span>
          ))}
        </div>
      )}

      <div className="toolbar">
        <span className="spacer" />
        {templates.data && templates.data.length > 0 && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowTemplates(true)}><Sparkles size={16} /> Da modello</button>
        )}
        <button type="button" className="btn btn-primary btn-sm" disabled={create.isPending} onClick={() => makePage()}>
          <Plus size={16} /> Nuova pagina
        </button>
      </div>

      {pages.isLoading && <PageLoader />}
      {pages.data && pages.data.length === 0 && (
        <div className="card"><EmptyState tone="neutral" title="Nessuna pagina qui">Crea la prima pagina o una dalle vostre pagine modello.</EmptyState></div>
      )}
      {pages.data && pages.data.length > 0 && (
        <div className="card list">
          {pages.data.map((p) => (
            <button key={p.id} type="button" className="list-row" onClick={() => openPage(p)}>
              <span className="list-icon" style={{ background: 'var(--ws-blue)', fontSize: 15 }}>{p.icon ?? <FileText size={16} />}</span>
              <span className="grow">{p.title || 'Senza titolo'}</span>
              <ChevronRight className="chev" size={20} />
            </button>
          ))}
        </div>
      )}

      <Sheet open={showTemplates} onClose={() => setShowTemplates(false)} variant="center" title="Crea da modello" labelledBy="tmpl-title">
        <div className="list">
          {(templates.data ?? []).map((t) => (
            <button key={t.id} type="button" className="list-row" onClick={() => makePage(t.id)}>
              <span className="list-icon" style={{ background: 'var(--ws-purple)', fontSize: 15 }}>{t.icon ?? <FileText size={16} />}</span>
              <span className="grow">{t.title || 'Senza titolo'}</span>
            </button>
          ))}
        </div>
      </Sheet>

      <PageEditorSheet
        id={openId}
        onClose={() => setOpenId(null)}
        onNavigate={(id, title) => { setOpenId(null); setStack((s) => [...s, { id, title }]); }}
      />
    </>
  );
}

function PageEditorSheet({ id, onClose, onNavigate }: { id: string | null; onClose: () => void; onNavigate: (id: string, title: string) => void }) {
  const { orgId } = useOrgCtx();
  const page = usePage(orgId, id);
  const update = useUpdatePage(orgId);
  const del = useDeletePage(orgId);
  const versions = usePageVersions(orgId, id);
  const restore = useRestorePageVersion(orgId);
  const toast = useToast();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [icon, setIcon] = useState<string | null>(null);
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [dirty, setDirty] = useState(false);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  if (page.data && loadedFor !== page.data.id) {
    setTitle(page.data.title);
    setIcon(page.data.icon);
    setBlocks(page.data.content.content.length ? page.data.content.content : [newBlock()]);
    setLoadedFor(page.data.id);
    setDirty(false);
  }

  const save = () => {
    if (!page.data) return;
    update.mutate(
      { id: page.data.id, patch: { title, icon, content: { type: 'doc', content: blocks.filter((b) => b.text.trim()) } } },
      { onSuccess: () => { setDirty(false); toast('Pagina salvata'); }, onError: (e) => toast(e instanceof ApiError ? e.message : 'Salvataggio non riuscito') },
    );
  };

  const setBlock = (i: number, patch: Partial<Block>) => { setBlocks((bs) => bs.map((b, j) => (j === i ? { ...b, ...patch } : b))); setDirty(true); };
  const addBlock = (i: number) => { setBlocks((bs) => [...bs.slice(0, i + 1), newBlock(), ...bs.slice(i + 1)]); setDirty(true); };
  const removeBlock = (i: number) => { setBlocks((bs) => (bs.length > 1 ? bs.filter((_, j) => j !== i) : bs)); setDirty(true); };

  return (
    <Sheet open={Boolean(id)} onClose={onClose} variant="side" full labelledBy="page-editor-title">
      {!page.data ? <PageLoader /> : (
        <>
          <h2 id="page-editor-title" className="sr-only">{page.data.title || 'Pagina senza titolo'}</h2>
          <div className="toolbar" style={{ marginBottom: 4 }}>
            {page.data.breadcrumb.slice(0, -1).map((b, i) => (
              <span key={b.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                {i > 0 && <ChevronRight size={14} color="var(--label-3)" />}
                <button type="button" className="btn btn-plain btn-sm" onClick={() => onNavigate(b.id, b.title)}>{b.icon} {b.title}</button>
              </span>
            ))}
            <span className="spacer" />
            {page.data.permissions.edit && (
              <button type="button" className="icon-btn muted" aria-label="Cronologia versioni" onClick={() => setHistoryOpen(true)}><History size={18} /></button>
            )}
            {page.data.permissions.delete && (
              <button type="button" className="icon-btn muted" aria-label="Elimina pagina"
                onClick={() => { if (confirm('Eliminare questa pagina?')) del.mutate({ id: page.data!.id, workspaceId: page.data!.workspaceId, parentPageId: page.data!.parentPageId }, { onSuccess: onClose }); }}>
                <Trash2 size={18} />
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14 }}>
            <select className="input" style={{ width: 52, textAlign: 'center', fontSize: 18, padding: '4px 0' }} value={icon ?? ''}
              disabled={!page.data.permissions.edit}
              onChange={(e) => { setIcon(e.target.value || null); setDirty(true); }}>
              <option value="">—</option>
              {ICONS.map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
            <input
              className="input" style={{ flex: 1, fontSize: 22, fontWeight: 700, border: 'none', background: 'transparent', padding: '4px 0' }}
              placeholder="Senza titolo" value={title} disabled={!page.data.permissions.edit}
              onChange={(e) => { setTitle(e.target.value); setDirty(true); }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {blocks.map((b, i) => (
              <div key={b.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                {b.type === 'checklist_item' && (
                  <input type="checkbox" checked={Boolean(b.checked)} disabled={!page.data!.permissions.edit} style={{ marginTop: 10 }} onChange={(e) => setBlock(i, { checked: e.target.checked })} />
                )}
                <select className="input" style={{ width: 118, fontSize: 12.5, padding: '4px 6px', flex: 'none' }} value={b.type} disabled={!page.data!.permissions.edit}
                  onChange={(e) => setBlock(i, { type: e.target.value as Block['type'] })}>
                  {BLOCK_TYPES.map((t) => <option key={t} value={t}>{BLOCK_TYPE_LABELS[t]}</option>)}
                </select>
                <textarea
                  className="textarea" rows={1} style={{
                    flex: 1, resize: 'none',
                    fontSize: b.type === 'heading1' ? 22 : b.type === 'heading2' ? 18 : 15,
                    fontWeight: b.type.startsWith('heading') ? 700 : 400,
                    fontStyle: b.type === 'quote' ? 'italic' : 'normal',
                    textDecoration: b.type === 'checklist_item' && b.checked ? 'line-through' : 'none',
                    color: b.type === 'checklist_item' && b.checked ? 'var(--label-3)' : 'inherit',
                  }}
                  value={b.text} disabled={!page.data.permissions.edit} placeholder={BLOCK_TYPE_LABELS[b.type]}
                  onChange={(e) => setBlock(i, { text: e.target.value })}
                />
                {page.data.permissions.edit && (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <button type="button" className="icon-btn muted" style={{ width: 26, height: 26 }} aria-label="Aggiungi blocco sotto" onClick={() => addBlock(i)}><Plus size={14} /></button>
                    {blocks.length > 1 && <button type="button" className="icon-btn muted" style={{ width: 26, height: 26 }} aria-label="Rimuovi blocco" onClick={() => removeBlock(i)}><Trash2 size={13} /></button>}
                  </div>
                )}
              </div>
            ))}
          </div>

          {page.data.permissions.edit && (
            <div className="sheet-foot" style={{ position: 'sticky', bottom: 0, marginTop: 16 }}>
              <button type="button" className="btn btn-primary" disabled={!dirty || update.isPending} onClick={save}>
                {update.isPending ? <Spinner /> : 'Salva'}
              </button>
            </div>
          )}

          <Sheet open={historyOpen} onClose={() => setHistoryOpen(false)} variant="center" title="Cronologia versioni" labelledBy="history-title">
            {!versions.data?.length && <p className="hint">Nessuna versione precedente ancora.</p>}
            <div className="list">
              {versions.data?.map((v) => (
                <button key={v.id} type="button" className="list-row" onClick={() => {
                  restore.mutate({ id: page.data!.id, versionId: v.id }, { onSuccess: () => { setHistoryOpen(false); setLoadedFor(null); toast('Versione ripristinata'); } });
                }}>
                  <span className="grow">
                    <div>{new Date(v.createdAt).toLocaleString('it-IT')} · {v.createdBy.displayName}</div>
                    <div className="secondary">{v.preview || '(vuota)'}</div>
                  </span>
                </button>
              ))}
            </div>
          </Sheet>
        </>
      )}
    </Sheet>
  );
}
